var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useState, useRef, useLayoutEffect } from 'react';
import useSelectList from '~/helpers/use-select-list';
import { useMainSticky } from '~/helpers/main-class-hooks';
import cn from 'classnames';
import './form-input.scss';
// Accessibility issues here:
// https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Reference/Attributes/aria-autocomplete
const LIMIT_SUGGESTIONS = 400;
function SuggestionItem({ value, accept, index, activeIndex, setActiveIndex }) {
    const ref = useRef(null);
    const active = index === activeIndex;
    useLayoutEffect(() => {
        var _a;
        if (active) {
            (_a = ref.current) === null || _a === void 0 ? void 0 : _a.scrollIntoView({ block: 'nearest' });
        }
    }, [active]);
    return (React.createElement("div", { className: cn('suggestion', { active }), ref: ref, onClick: () => accept(value), onMouseMove: () => setActiveIndex(index) }, value));
}
function useMatches(pattern, suggestions = []) {
    const matches = React.useMemo(() => pattern.length > 1
        ? suggestions.filter((s) => s.toLowerCase().includes(pattern))
        : [], [pattern, suggestions]);
    const exactMatch = React.useMemo(() => matches.includes(pattern) ||
        (matches.length === 1 && pattern === matches[0].toLowerCase()), [matches, pattern]);
    return [matches, exactMatch];
}
function SuggestionBox({ matches, exactMatch, accepted, accept, activeIndex, setActiveIndex }) {
    if (exactMatch) {
        accept(matches[0]);
    }
    useMainSticky();
    return (React.createElement("div", { className: "suggestions" },
        React.createElement("div", { className: "suggestion-box" },
            !exactMatch &&
                !accepted &&
                matches
                    .slice(0, LIMIT_SUGGESTIONS)
                    .map((match, i) => (React.createElement(SuggestionItem, { value: match, accept: accept, index: i, activeIndex: activeIndex, setActiveIndex: setActiveIndex, key: match }))),
            matches.length > LIMIT_SUGGESTIONS && (React.createElement("div", { className: "suggestion" },
                React.createElement("i", null, "List truncated"))))));
}
function ValidatingInput({ value, inputProps, onChange, accepted }) {
    const [validationMessage, setValidationMessage] = useState('');
    const ref = useRef(null);
    const Tag = inputProps.Tag || 'input';
    useLayoutEffect(() => {
        var _a;
        const vm = (_a = ref.current) === null || _a === void 0 ? void 0 : _a.validationMessage;
        setValidationMessage(vm);
        if (!vm && !accepted) {
            setValidationMessage('Not a valid selection');
        }
    }, [value, accepted]);
    return (React.createElement(React.Fragment, null,
        React.createElement(Tag, Object.assign({ ref: ref, onChange: onChange, value: value }, inputProps)),
        React.createElement("div", { className: "invalid-message" }, validationMessage)));
}
// eslint-disable-next-line complexity
export default function FormInput({ label, longLabel, inputProps, suggestions }) {
    var _a, _b;
    const [value, setValue] = useState((_b = (_a = inputProps.value) === null || _a === void 0 ? void 0 : _a.toString()) !== null && _b !== void 0 ? _b : '');
    const { onChange: otherOnChange } = inputProps, otherProps = __rest(inputProps, ["onChange"]);
    const [matches, exactMatch] = useMatches(value.toString().toLowerCase(), suggestions);
    const [accepted, setAccepted] = useState(!(suggestions === null || suggestions === void 0 ? void 0 : suggestions.length));
    const accept = React.useCallback((item) => {
        setValue(item);
        setAccepted(true);
        if (otherOnChange) {
            otherOnChange({
                target: { value: item }
            });
        }
    }, [otherOnChange]);
    const [activeIndex, handleKeyDown, setActiveIndex] = useSelectList({
        getItems: () => matches,
        accept,
        searchable: false
    });
    const onChange = React.useCallback((event) => {
        if (otherOnChange) {
            otherOnChange(event);
        }
        setValue(event.target.value);
        // Fields without suggestions should be considered accepted
        // Empty suggestion lists will still be unaccepted
        setAccepted(!suggestions);
    }, [otherOnChange, suggestions]);
    const onKeyDown = React.useCallback((event) => {
        // Don't use space to accept
        if (event.key === ' ') {
            return;
        }
        if (handleKeyDown(event)) {
            event.preventDefault();
            event.stopPropagation();
        }
    }, [handleKeyDown]);
    return (React.createElement("label", { className: "form-input" },
        React.createElement("div", { className: "control-group" },
            label && React.createElement("label", { className: "field-label" }, label),
            longLabel && (React.createElement("label", { className: "field-long-label" }, longLabel)),
            React.createElement(ValidatingInput, { value: value, inputProps: Object.assign({ onKeyDown }, otherProps), onChange: onChange, accepted: accepted }),
            suggestions && (React.createElement(SuggestionBox, { matches: matches, exactMatch: exactMatch, accepted: accepted, accept: accept, activeIndex: activeIndex, setActiveIndex: setActiveIndex })))));
}
